import { useMemo, useState } from "react";
import { articles, categoryOptions } from "@/mocks/media";

export default function Reading() {
  const [activeCat, setActiveCat] = useState("all");
  const [keyword, setKeyword] = useState("");

  const list = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return articles.filter((a) => {
      const catOk = activeCat === "all" || a.category === activeCat;
      const kwOk =
        !kw ||
        `${a.title}${a.summary}${a.tag}${a.category}`.toLowerCase().includes(kw);
      return catOk && kwOk;
    });
  }, [activeCat, keyword]);

  return (
    <section id="reading" className="bg-background-100 py-16 md:py-20">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-primary-500" />
              <span className="text-xs font-medium uppercase tracking-[0.25em] text-primary-500">
                READING ROOM
              </span>
            </div>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              深度解读
            </h2>
            <p className="mt-2 max-w-xl text-sm text-foreground-600 md:text-base">
              按主题筛阅，或用关键字检索你关心的攻防议题。
            </p>
          </div>

          <div className="flex w-full flex-col gap-3 sm:flex-row lg:w-auto lg:items-center">
            <div className="relative sm:w-72">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-500" />
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索关键词，如：律师函 / 反转"
                className="h-11 w-full rounded-full border border-background-300 bg-background-50 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {categoryOptions.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setActiveCat(c.key)}
              className={`whitespace-nowrap rounded-full px-1 py-1 ${
                activeCat === c.key ? "bg-primary-500 text-background-50" : "bg-transparent text-foreground-700"
              }`}
            >
              <span
                className={`inline-flex w-full items-center justify-center whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  activeCat === c.key
                    ? "text-background-50"
                    : "bg-background-50 hover:bg-background-200"
                }`}
              >
                {c.label}
              </span>
            </button>
          ))}
          <span className="ml-auto hidden items-center whitespace-nowrap text-xs text-foreground-500 sm:inline-flex">
            共 {list.length} 篇匹配
          </span>
        </div>

        {list.length === 0 ? (
          <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
            <i className="ri-inbox-2-line text-3xl text-foreground-400" />
            <p className="mt-3 text-sm text-foreground-600">没有找到匹配的内容，换个关键词或分类试试。</p>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setActiveCat("all");
              }}
              className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-600"
            >
              重置筛选
            </button>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((a, i) => (
              <article
                key={a.id}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all hover:border-primary-200"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={a.image}
                    alt={a.alt}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-foreground-950/80 px-2.5 py-1 text-[11px] text-background-50 backdrop-blur">
                    {a.category}
                  </span>
                  {i === 0 && (
                    <span className="absolute right-3 top-3 rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-medium text-foreground-950">
                      编辑精选
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-primary-500">{a.kind}</p>
                  <h3 className="mt-2 font-heading text-base font-semibold leading-snug text-foreground-950 md:text-lg">
                    {a.title}
                  </h3>
                  <p className="mt-2 line-clamp-3 flex-1 text-sm leading-6 text-foreground-600">{a.summary}</p>
                  <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-user-line" />
                      {a.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line" />
                      {a.date} · {a.readTime}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}